/**
 * Platform-default cancellation policy admin API.
 *
 * Endpoints (editable since migration 0051 — `platform_settings` singleton):
 *   GET /admin/cancellation-policy/default → getPlatformDefault
 *     `{ policy, updatedAt, source }` — `updatedAt` is the If-Match token.
 *   PUT /admin/cancellation-policy/default → putPlatformDefault
 *     If-Match guarded (428 absent / 409 STALE_PLATFORM_DEFAULT on mismatch).
 *
 * On a GET failure (network) we degrade to the hard-coded
 * `PLATFORM_DEFAULT_CANCELLATION_POLICY` with `updatedAt: null` — the editor
 * still renders the canonical tier table, but Save stays disabled (no token).
 */
import type { CancellationPolicy } from '@bravemove/types';
import { PLATFORM_DEFAULT_CANCELLATION_POLICY } from '@bravemove/types';
import { adminFetch } from '../auth/apiClient.js';

interface DataEnvelope<T> {
  readonly data: T;
}

export interface PlatformDefaultRead {
  readonly policy: CancellationPolicy;
  /** If-Match token for the PUT; null when the GET failed (Save disabled). */
  readonly updatedAt: string | null;
  readonly source: 'stored' | 'constant';
}

export async function getPlatformDefault(): Promise<PlatformDefaultRead> {
  try {
    const body = await adminFetch<
      DataEnvelope<{ policy: CancellationPolicy; updatedAt: string; source: 'stored' | 'constant' }>
    >('/admin/cancellation-policy/default');
    return body.data;
  } catch {
    // Network/permission failure — degrade to the constant; Save disabled.
    return {
      policy: PLATFORM_DEFAULT_CANCELLATION_POLICY,
      updatedAt: null,
      source: 'constant',
    };
  }
}

export async function putPlatformDefault(
  policy: CancellationPolicy,
  ifMatch: string,
): Promise<{ policy: CancellationPolicy; updatedAt: string }> {
  const body = await adminFetch<DataEnvelope<{ policy: CancellationPolicy; updatedAt: string }>>(
    '/admin/cancellation-policy/default',
    {
      method: 'PUT',
      headers: { 'if-match': ifMatch, 'content-type': 'application/json' },
      body: JSON.stringify(policy),
    },
  );
  return body.data;
}
